import * as Icon from "@iyk/icons"
import * as UI from "@iyk/ui"
import * as RadioGroup from "@radix-ui/react-radio-group"
import * as React from "react"
import * as Carousel from "./ui/carousel.tsx"

import { each } from "@iyk/flow"
import { Form } from "@remix-run/react"
import { cva } from "class-variance-authority"
import { setThemeTo } from "../ui/themes.ts"
import { useClickOutside } from "../ui/use-click-outside.ts"
import { useIntersection } from "../ui/use-intersection.ts"
import { PACKS } from "./packs.ts"
import { useTracking } from "./tracking-provider.tsx"
import { Events } from "./Tracking.ts"
import { SectionLayout } from "./ui/section-layout.tsx"

//#region ShopNow

const SHOP_BUTTON_HEIGHT = 48
const SHOP_BUTTON_MARGIN = 250

export const ShopNow = ({ className, ...props }: ShopNowProps) => {
  const tracking = useTracking()
  const [selectedPackVariant, setSelectedPackVariant] = React.useState(PACKS[0].variant)
  const [quantity, setQuantity] = React.useState(1)

  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)

  const [shopNowButtonIsVisible, setShopNowButtonIsVisible] = React.useState(false)
  const [shopNowContentIsVisible, setShopNowContentIsVisible] = React.useState(false)

  const [isShopNowStickyBetweenMargin, setIsShopNowStickyBetweenMargin] = React.useState(false)

  const shopNowStickyRef = React.useRef<HTMLDivElement>(null)
  useClickOutside(shopNowStickyRef, () => setIsExpanded(false))

  const shopNowButtonRef = useIntersection<HTMLButtonElement>({
    onIntersect(entry: IntersectionObserverEntry) {
      setShopNowButtonIsVisible(entry.isIntersecting)
    },
    options: {
      threshold: 0,
      rootMargin: `0px 0px -${SHOP_BUTTON_HEIGHT - 4}px`, // 4px is the border-top size
    },
  })

  const toggleShopNowButtonVisibility = useIntersection<HTMLButtonElement>({
    onIntersect(entry: IntersectionObserverEntry) {
      if (entry.isIntersecting) {
        setIsShopNowStickyBetweenMargin(true)
        if (isExpanded) {
          setIsExpanded(false)
        }
      } else {
        setIsShopNowStickyBetweenMargin(false)
      }
    },
    options: {
      threshold: 0,
      rootMargin: `0px 0px ${SHOP_BUTTON_MARGIN}px 0px`,
    },
  })

  const shopNowContentRef = useIntersection<HTMLDivElement>({
    onIntersect(entry: IntersectionObserverEntry) {
      setShopNowContentIsVisible(entry.isIntersecting)
    },
    options: {
      threshold: 0,
    },
  })

  const showStickyShopNow = !shopNowButtonIsVisible && !shopNowContentIsVisible

  const scrollToShopNow = () => {
    const shopNowElement = document.getElementById("shop-now")
    shopNowElement?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  return (
    <>
      <SectionLayout
        id="shop-now"
        size="container"
        theme="dark"
        className={classForShopNow({ className })}
        {...props}
      >
        <p className="text-base text-center lg:text-start lg:text-2xl text-gray-12 order-2 lg:order-1">
          Signed, sealed, and
          <br />
          forever connected
        </p>
        <div
          className={classForStickyShopNow({
            isFixed: false,
            className: "order-1 lg:order-2",
          })}
        >
          <button
            ref={UI.extendRef(shopNowButtonRef, toggleShopNowButtonVisibility)}
            style={{ height: SHOP_BUTTON_HEIGHT }}
            className={classForShopNowButton()}
          >
            Shop Now
            <Icon.ArrowUp aria-hidden="true" />
          </button>
          <div ref={shopNowContentRef}>
            <ShopNowCarousel />
            <ShopCheckout
              idPrefix="static"
              selectedPackVariant={selectedPackVariant}
              setSelectedPackVariant={setSelectedPackVariant}
              quantity={quantity}
              setQuantity={setQuantity}
            />
          </div>
        </div>
      </SectionLayout>
      <div
        ref={shopNowStickyRef}
        className={classForStickyShopNow({
          isExpanded,
          isHovered,
          isShowed: showStickyShopNow,
          isFixed: true,
        })}
        onClick={() => {
          if (isHovered) {
            setIsHovered(false)
          }
          if (isShopNowStickyBetweenMargin) {
            scrollToShopNow()
          } else {
            setIsExpanded((prev) => !prev)
          }

          if (!isExpanded) {
            tracking.track(Events.SHOP_NOW_CLICKED, {})
          }
        }}
        onMouseEnter={() => {
          const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0
          if (!isExpanded && !isTouchDevice) {
            setIsHovered(true)
          }
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button style={{ height: SHOP_BUTTON_HEIGHT }} className={classForShopNowButton()}>
          Shop Now
          {isExpanded ? <Icon.ArrowDown aria-hidden="true" /> : <Icon.ArrowUp aria-hidden="true" />}
        </button>
        <ShopNowCarousel isStickyButtonHovered={isHovered} />
        <ShopCheckout
          idPrefix="sticky"
          selectedPackVariant={selectedPackVariant}
          setSelectedPackVariant={setSelectedPackVariant}
          quantity={quantity}
          setQuantity={setQuantity}
        />
      </div>
    </>
  )
}

const classForShopNow = cva([
  "grid grid-cols-1 items-center gap-24 lg:gap-[85px] lg:grid-cols-[1fr_max(12px,50dvw-190px)_1fr]",
])

const classForShopNowButton = cva([
  "shrink-0 w-full",
  "flex items-center justify-between p-3",
  "text-sm",
  "rounded-t",
  "bshadow-b bshadow-gray-5",
  "cursor-pointer",
  setThemeTo("light"),
])

const classForStickyShopNow = cva(
  [
    setThemeTo("light"),
    "flex flex-col",
    "border-[rgba(0,0,0,0.04)]",
    "bg-white mx-4 md:max-w-[380px] md:mx-auto",
    "overflow-hidden",
    "transition-transform duration-500 ease-in-out",
    "z-10",
  ],
  {
    variants: {
      isExpanded: {
        true: "translate-y-0",
      },
      isHovered: {
        true: "translate-y-[calc(100%-80px)]",
      },
      isShowed: {
        true: "block",
        false: "hidden",
      },
      isFixed: {
        true: ["fixed bottom-0 left-0 right-0", "rounded-t", "border-x-4 border-t-4"],
        false: ["w-full rounded mx-auto", "self-center", "border-4"],
      },
    },
    compoundVariants: [
      {
        isExpanded: false,
        isFixed: true,
        className: "translate-y-[calc(100%-52px)]",
      },
    ],
  },
)

type ShopNowProps = JSX.IntrinsicElements["div"]

//#endregion

//#region Carousel

const ShopNowCarousel = ({
  className,
  isStickyButtonHovered = false,
}: {
  className?: string
  isStickyButtonHovered?: boolean
}) => {
  return (
    <Carousel.Root
      onClick={(e) => {
        if (!isStickyButtonHovered) {
          e.stopPropagation()
        }
      }}
      className={classNameForShopNowCarousel({
        isClickable: isStickyButtonHovered,
        className,
      })}
    >
      <Carousel.Slides>
        {each(SLIDES, (slide) => (
          <Carousel.Slide
            key={slide.alt}
            className="flex-[0_0_100%] h-full flex items-center justify-center"
          >
            <img
              className="size-full object-contain"
              src={slide.src}
              alt={slide.alt}
              width={slide.width}
              height={slide.height}
              loading="lazy"
            />
          </Carousel.Slide>
        ))}
      </Carousel.Slides>
      <Carousel.Controls className="gap-2" />
    </Carousel.Root>
  )
}

const classNameForShopNowCarousel = cva(
  ["h-full flex flex-col items-center justify-center gap-2 p-3"],
  {
    variants: {
      isClickable: {
        true: "cursor-pointer",
      },
    },
  },
)

const SLIDES = [
  {
    src: "https://assets.iyk-dev.app/postcards/pdp-01.webp",
    alt: "Postcard 1",
    width: 712,
    height: 712,
  },
  {
    src: "https://assets.iyk-dev.app/postcards/pdp-02.webp",
    alt: "Postcard 2",
    width: 712,
    height: 712,
  },
  {
    src: "https://assets.iyk-dev.app/postcards/pdp-03.webp",
    alt: "Postcard 3",
    width: 712,
    height: 712,
  },
  {
    src: "https://assets.iyk-dev.app/postcards/pdp-04.webp",
    alt: "Postcard 4",
    width: 712,
    height: 712,
  },
]

//#endregion

//#region Checkout

const ShopCheckout = ({
  idPrefix,
  selectedPackVariant,
  setSelectedPackVariant,
  quantity,
  setQuantity,
}: {
  idPrefix: string
  selectedPackVariant: string
  setSelectedPackVariant: (value: string) => void
  quantity: number
  setQuantity: (value: number) => void
}) => {
  const tracking = useTracking()
  const selectedPack = PACKS.find((pack) => pack.variant === selectedPackVariant) ?? PACKS[0]
  const checkoutPrice = ((selectedPack.total ?? 0) * quantity).toFixed(2)

  return (
    <Form
      method="post"
      className="shrink-0 grid pt-3 px-3 pb-[42px]"
      onClick={(e) => e.stopPropagation()}
      onSubmit={() => {
        tracking.track(Events.CHECKOUT_STARTED, {
          "Pack Variant": selectedPackVariant,
          Quantity: quantity,
          "Total Price": parseFloat(checkoutPrice),
        })
      }}
    >
      <RadioGroup.Root
        name="variant"
        defaultValue={PACKS[0].variant}
        value={selectedPackVariant}
        onValueChange={setSelectedPackVariant}
        className="flex w-full"
      >
        {each(PACKS, (pack, index) => {
          const priceLabel = `$${pack.pricePerCard} ${pack.variant === "PackOfOne" ? "" : "each"}`
          return (
            <div className="flex-1" key={pack.variant}>
              <RadioGroup.Item
                value={pack.variant}
                id={`${idPrefix}${pack.variant}`}
                className="peer sr-only"
              />
              <label
                htmlFor={`${idPrefix}${pack.variant}`}
                className={classForRadioGroupItem({
                  left: index === 0,
                  right: index === PACKS.length - 1,
                  middle: index === Math.floor(PACKS.length / 2),
                })}
              >
                <span className="text-xs">{pack.label}</span>
                <span className="text-xs opacity-60">{priceLabel}</span>
              </label>
            </div>
          )
        })}
      </RadioGroup.Root>
      <div className="flex items-center justify-between bshadow bshadow-gray-12 h-12 px-1.5">
        <UI.Button
          type="button"
          variant="ghost"
          onClick={() => {
            setQuantity(Math.max(1, quantity - 1))
          }}
          aria-label="Reduce quantity by 1"
        >
          <Icon.Minus />
        </UI.Button>
        <span className="text-sm">Qty — {quantity}</span>
        <UI.Button
          type="button"
          variant="ghost"
          onClick={() => {
            setQuantity(quantity + 1)
          }}
          aria-label="Increase quantity by 1"
        >
          <Icon.Plus />
        </UI.Button>
      </div>
      <input type="hidden" name="qty" value={quantity} />
      <UI.Button type="submit" size="lg" className="w-full rounded-b-md">
        Checkout — ${checkoutPrice}
      </UI.Button>
    </Form>
  )
}

const classForRadioGroupItem = UI.cva(
  [
    // Base
    "h-12 outline-none cursor-pointer",
    "flex flex-col items-center justify-center gap-0.5",
    "after:bshadow-gray-12",
    // Background
    "after:absolute after:inset-0 after:content-[''] after:bg-transparent after:z-[-1]",
    // Checked
    "peer-data-[state=checked]:after:bg-gray-12",
    "peer-data-[state=checked]:text-gray-1",
    // Unchecked
    "peer-data-[state=unchecked]:hover:after:bg-gray-3 relative",
  ],
  {
    variants: {
      left: {
        true: "after:rounded-tl-md after:bshadow-lrt",
      },
      right: {
        true: "after:rounded-tr-md after:bshadow-lrt",
      },
      middle: {
        true: "after:bshadow-t",
      },
    },
  },
)
//#endregion
